if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.normalizeSellingPlan(formData);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      normalizeSellingPlan(formData) {
        const purchaseOption = this.getCurrentPurchaseOption();
        const sellingPlanInputs = this.getSellingPlanInputs();

        if (purchaseOption.type === 'one_time') {
          formData.delete('selling_plan');
          sellingPlanInputs.forEach((input) => {
            input.value = '';
            input.disabled = true;
          });
          return;
        }

        if (purchaseOption.type === 'subscription') {
          const sellingPlanId = purchaseOption.sellingPlanId || this.getSellingPlanInputValue(sellingPlanInputs);

          if (sellingPlanId) {
            formData.set('selling_plan', sellingPlanId);
            sellingPlanInputs.forEach((input) => {
              input.disabled = false;
              input.value = sellingPlanId;
            });
            return;
          }
        }

        if (!formData.get('selling_plan')) {
          formData.delete('selling_plan');
        }
      }

      getProductSection() {
        return this.closest('[id^="MainProduct-"]') || this.form.closest('[id^="MainProduct-"]') || document;
      }

      getCurrentVariantId() {
        const variantInput = this.form.querySelector('[name="id"]');

        return variantInput && variantInput.value ? String(variantInput.value) : '';
      }

      getPurchaseOptionScope(productSection) {
        const currentVariantId = this.getCurrentVariantId();

        if (!currentVariantId || !productSection.querySelectorAll) return productSection;

        const variantBlocks = productSection.querySelectorAll('[data-variant-id]');
        const currentVariantBlock = [...variantBlocks].find((block) => {
          return String(block.getAttribute('data-variant-id')) === currentVariantId;
        });

        return currentVariantBlock || productSection;
      }

      getCurrentPurchaseOption() {
        const productSection = this.getProductSection();
        const purchaseOptionScope = this.getPurchaseOptionScope(productSection);
        const checkedSubscriptionTab = purchaseOptionScope.querySelector('input[id^="tab_subscribe_"]:checked');
        const checkedOneTimeTab = purchaseOptionScope.querySelector('input[id^="tab_otp_"]:checked');

        if (checkedOneTimeTab) {
          return { type: 'one_time', sellingPlanId: '' };
        }

        if (checkedSubscriptionTab) {
          const checkedPlan = purchaseOptionScope.querySelector('input[data-radio-type="selling_plan"]:checked');

          return {
            type: 'subscription',
            sellingPlanId: checkedPlan ? checkedPlan.getAttribute('data-selling-plan-id') || checkedPlan.value || '' : '',
          };
        }

        const checkedPurchaseOption = purchaseOptionScope.querySelector(
          'input[data-radio-type="one_time_purchase"]:checked, input[data-radio-type="selling_plan"]:checked'
        );

        if (checkedPurchaseOption) {
          const radioType = checkedPurchaseOption.getAttribute('data-radio-type');

          if (radioType === 'one_time_purchase') {
            return { type: 'one_time', sellingPlanId: '' };
          }

          if (radioType === 'selling_plan') {
            return {
              type: 'subscription',
              sellingPlanId:
                checkedPurchaseOption.getAttribute('data-selling-plan-id') || checkedPurchaseOption.value || '',
            };
          }
        }

        return { type: '', sellingPlanId: '' };
      }

      getSellingPlanInputs() {
        const productSection = this.getProductSection();
        const candidates = [
          ...this.form.querySelectorAll('[name="selling_plan"]'),
          ...productSection.querySelectorAll('[name="selling_plan"]'),
          ...document.querySelectorAll('[name="selling_plan"][form]'),
        ];

        return [...new Set(candidates)].filter((input) => {
          return (
            this.form.contains(input) ||
            input.form === this.form ||
            input.getAttribute('form') === this.form.id ||
            (productSection !== document && productSection.contains(input))
          );
        });
      }

      getSellingPlanInputValue(inputs) {
        const checkedInput = inputs.find((input) => input.checked && input.value);

        if (checkedInput) return checkedInput.value;

        const selectedOption = inputs.find((input) => input.tagName === 'SELECT' && input.value);

        if (selectedOption) return selectedOption.value;

        const hiddenInput = inputs.find((input) => input.type === 'hidden' && input.value);

        return hiddenInput ? hiddenInput.value : '';
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}
